/**
 * Detention Exposure Engine v2
 *
 * Deterministic detention billing computation for active dock occupants.
 * Proper billing math: ceil((dwell - freeTime) / 60) × rate
 */

const FREE_MINUTES = 120;
const RATE_PER_HOUR = 75;
const PROJECTION_MINUTES = 120; // 2-hour projection horizon

export interface DetentionTruck {
    dockId: string;
    carrierName: string;
    dwellMinutes: number;
    billableHours: number;
    currentCost: number;
    projectedHours: number;
    projectedCost: number;
}

export interface DetentionExposureResult {
    currentCost: number;
    projectedCost: number;
    totalCost: number;
    trucksAtRisk: number;
    contributors: DetentionTruck[];
}

/**
 * Compute billable hours using proper detention rounding.
 * dwell <= free time → 0
 * 2h10m → 1 hour (ceil of (130-120)/60 = ceil(0.167) = 1)
 * 2h59m → 1 hour (ceil of (179-120)/60 = ceil(0.983) = 1)
 * 3h01m → 2 hours (ceil of (181-120)/60 = ceil(1.017) = 2)
 */
export function computeBillableHours(dwellMinutes: number, freeMinutes: number = FREE_MINUTES): number {
    return Math.max(0, Math.ceil((dwellMinutes - freeMinutes) / 60));
}

interface OccupantInput {
    checkInTime?: string | null;
    startAt?: string | null;
    dockId?: string;
    carrierName?: string;
    carrierMcNumber?: string;
}

/**
 * Compute detention exposure for active dock occupants.
 *
 * Returns current cost (already billable), projected cost
 * (additional exposure if trucks stay 2 more hours), total,
 * count of trucks at risk, and top 3 contributors.
 */
export function computeDetentionExposure(
    activeOccupants: OccupantInput[],
    now: number
): DetentionExposureResult {
    const trucks: DetentionTruck[] = [];

    for (const occ of activeOccupants) {
        if (!occ.checkInTime) continue;
        const checkInMs = new Date(occ.checkInTime).getTime();
        if (isNaN(checkInMs)) continue;

        // Detention clock starts at max(checkInTime, startAt)
        // Early arrivals don't start the clock before the scheduled window
        const startAtMs = occ.startAt ? new Date(occ.startAt).getTime() : NaN;
        const clockStart = !isNaN(startAtMs) ? Math.max(checkInMs, startAtMs) : checkInMs;

        const dwellMinutes = Math.max(0, (now - clockStart) / 60000);
        const projectedDwellMinutes = dwellMinutes + PROJECTION_MINUTES;

        const billableHours = computeBillableHours(dwellMinutes);
        const projectedBillableHours = computeBillableHours(projectedDwellMinutes);
        const projectedAdditionalHours = Math.max(0, projectedBillableHours - billableHours);

        // Only include if there's current or projected cost
        if (billableHours === 0 && projectedAdditionalHours === 0) continue;

        const currentCost = billableHours * RATE_PER_HOUR;
        const projectedCost = projectedAdditionalHours * RATE_PER_HOUR;
        const dockId = occ.dockId || '?';
        const carrierName = occ.carrierName || (occ.carrierMcNumber ? `MC ${occ.carrierMcNumber}` : 'Unknown');

        trucks.push({
            dockId,
            carrierName,
            dwellMinutes: Math.round(dwellMinutes),
            billableHours,
            currentCost,
            projectedHours: projectedAdditionalHours,
            projectedCost,
        });
    }

    // Sort by total cost (current + projected) descending
    trucks.sort((a, b) => (b.currentCost + b.projectedCost) - (a.currentCost + a.projectedCost));

    const currentCost = trucks.reduce((s, t) => s + t.currentCost, 0);
    const projectedCost = trucks.reduce((s, t) => s + t.projectedCost, 0);

    return {
        currentCost,
        projectedCost,
        totalCost: currentCost + projectedCost,
        trucksAtRisk: trucks.length,
        contributors: trucks.slice(0, 3),
    };
}
